import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Button from '../ui/Button';
import Avatar from '../ui/Avatar';
import RegistrationModal from '../ui/RegistrationModal';
import { useTheme } from '../../context/ThemeContext';
import Loader from '../shared/Loader';
import { NavLink } from "react-router-dom";
import { authUtils } from '../../utils/auth';

const Navbar = ({ user = null, isAuthenticated = false, onLogout = () => {} }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showRegisterDropdown, setShowRegisterDropdown] = useState(false);
  const [showMobileRegistrationModal, setShowMobileRegistrationModal] = useState(false);
  const location = useLocation();
  const { theme, setTheme, toggleTheme } = useTheme();
  const [showThemeMenu, setShowThemeMenu] = useState(false);
  const themeEmoji = theme === 'dark' ? '🌙' : theme === 'golden' ? '🌟' : '☀️';

  // Helper function to get dashboard URL based on user role
  const getDashboardUrl = () => {
    if (!isAuthenticated) return '/admin';
    
    const tokenData = authUtils.decodeToken();
    const userRole = tokenData?.role || tokenData?.userType;
    
    if (userRole === 'Student') {
      return '/student';
    } else if (userRole === 'Instructor') {
      return '/instructor';
    } else {
      return '/admin';
    }
  };

  const getProfileUrl = () => {
    if (!isAuthenticated) return '/login';
    const tokenData = authUtils.decodeToken();
    const userRole = tokenData?.role || tokenData?.userType;
    if (userRole === 'Student') return '/student/profile';
    if (userRole === 'Instructor') return '/instructor/profile';
    return '/admin/profile';
  };

  const getSettingsUrl = () => {
    if (!isAuthenticated) return '/login';
    const tokenData = authUtils.decodeToken();
    const userRole = tokenData?.role || tokenData?.userType;
    if (userRole === 'Student') return '/student/settings';
    if (userRole === 'Instructor') return '/instructor/settings';
    return '/admin/settings';
  };
  {/*const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => setLoading(false), 800);
    return () => clearTimeout(t);
  }, [location.pathname]);
  
  useEffect(() => {
    // Simulate data loading or animation delay
    const timer = setTimeout(() => setLoading(false), 1000);
    return () => clearTimeout(timer);
  }, []);
  */}
  
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showRegisterDropdown) {
        const dropdownElement = document.querySelector('.registration-dropdown');
        if (dropdownElement && !dropdownElement.contains(event.target)) {
          setShowRegisterDropdown(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showRegisterDropdown]);


  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/courses', label: 'Courses' },
    { path: '/pre-register', label: 'Pre-Register' },
    { path: '/faculty', label: 'Faculty' },
    { path: '/homeschooling', label: 'Homeschooling' },
    { path: '/articles', label: 'Articles' },
    { path: '/blogs', label: 'Blogs' },
    { path: '/counseling', label: 'Counseling' },
    { path: '/about', label: 'About' },
    { path: '/contact', label: 'Contact' }
  ];

  return (
    <>
   {/* <Loader visible={loading} />*/}
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 py-2
      bg-[var(--color-surface)] border-b border-[var(--color-border)]
      ${isScrolled ? 'shadow-2xl' : 'shadow-lg'}
    `}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-12">
          {/* Logo */}
          <NavLink to="/" className="flex items-center gap-2 group">
            <img
              src="/logo.png"
              alt="SeerahVerse Logo"
              className="h-8 w-auto"
            />
            <div className="flex flex-col justify-center">
              <span className="font-bold text-sm bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-secondary)] bg-clip-text text-transparent leading-tight">
               SEERAHVERSE
              </span>
              <span className="text-xs font-semibold text-[var(--color-text-secondary)] tracking-[0.5em] uppercase">
                Academia
              </span>
            </div>
          </NavLink>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-3">
            {navLinks.map((link) => (
              <NavLink
                key={link.path}
                to={link.path}
                className={`
                  text-sm font-medium transition-all duration-300 relative px-3 py-1.5 rounded-lg h-8 flex items-center
                  ${location.pathname === link.path
                    ? 'text-[var(--color-text-inverse)] bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-secondary)] shadow-lg'
                    : 'text-[var(--color-text-secondary)] hover:text-[var(--color-primary)] hover:bg-[var(--color-surface-hover)]'}
                  after:content-[''] after:absolute after:bottom-[-2px] after:left-0 after:w-full after:h-0.5
                  after:bg-gradient-to-r after:from-[var(--color-primary)] after:to-[var(--color-secondary)] after:transform after:scale-x-0 after:transition-transform
                  ${location.pathname === link.path ? 'after:scale-x-100' : 'hover:after:scale-x-100'}
                `}
              >
                  {link.label}
              </NavLink>
            ))}
          </div>

          {/* User Actions */}
          <div className="hidden lg:flex items-center gap-2">
            {/* Theme Dropdown (icons only) */}
            <div className="relative">
              <button
                onClick={() => setShowThemeMenu((s) => !s)}
                className="p-1.5 rounded-lg transition-colors hover:bg-[var(--color-surface-hover)] border border-[var(--color-border)] h-8 w-8 flex items-center justify-center"
                aria-label="Theme menu"
              >
                <span className="text-sm leading-none" role="img" aria-label="theme-icon">{themeEmoji}</span>
              </button>
              <div className={`absolute right-0 mt-2 w-24 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl shadow-xl py-2 ${showThemeMenu ? 'opacity-100 visible' : 'opacity-0 invisible'} transition-opacity`}>
                <button onClick={() => { setTheme('light'); setShowThemeMenu(false); }} className="w-full px-3 py-2 hover:bg-[var(--color-surface-hover)] flex items-center justify-center text-xl rounded-lg transition-colors" aria-label="Light theme">☀️</button>
                <button onClick={() => { setTheme('dark'); setShowThemeMenu(false); }} className="w-full px-3 py-2 hover:bg-[var(--color-surface-hover)] flex items-center justify-center text-xl rounded-lg transition-colors" aria-label="Dark theme">🌙</button>
                <button onClick={() => { setTheme('golden'); setShowThemeMenu(false); }} className="w-full px-3 py-2 hover:bg-[var(--color-surface-hover)] flex items-center justify-center text-xl rounded-lg transition-colors" aria-label="Golden theme">🌟</button>
              </div>
            </div>

            {isAuthenticated && user ? (
              <div className="flex items-center gap-2">
                {/* Dashboard Link */}
                <NavLink to={getDashboardUrl()} className="p-1.5 rounded-lg transition-colors hover:bg-[var(--color-surface-hover)] border border-[var(--color-border)] h-8 px-3 flex items-center justify-center text-sm font-medium text-[var(--color-text-secondary)] hover:text-[var(--color-primary)]">
                  Go to Dashboard
                </NavLink>

                <button className="hidden p-1.5 rounded-lg transition-colors hover:bg-[var(--color-surface-hover)] border border-[var(--color-border)] h-8 w-8 flex items-center justify-center">
                  <svg className="w-4 h-4 text-[var(--color-text-secondary)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </button>

                <div className="relative group">
                  <div className="flex items-center gap-2 px-3 py-1 rounded-lg bg-[var(--color-surface-hover)] cursor-pointer hover:bg-[var(--color-primary-light)] transition-colors">
                    <Avatar
                      src={user.avatar}
                      name={`${user.firstName} ${user.lastName}`}
                      size="sm"
                      className="cursor-pointer"
                    />
                    <span className="text-sm font-medium text-[var(--color-text-primary)]">
                      {user.firstName} {user.lastName}
                    </span>
                  </div>
                  <div className="absolute right-0 mt-2 w-60 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-2xl shadow-2xl p-2 space-y-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                    <NavLink
                      to={getDashboardUrl()}
                      className="flex items-center gap-3 px-4 py-2.5 text-[var(--color-text-primary)] hover:bg-[var(--color-surface-hover)] rounded-xl transition-all duration-200"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      <span className="font-medium">Dashboard</span>
                    </NavLink>
                    <NavLink
                      to={getProfileUrl()}
                      className="flex items-center gap-3 px-4 py-2.5 text-[var(--color-text-primary)] hover:bg-[var(--color-surface-hover)] rounded-xl transition-all duration-200"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v1h16v-1c0-2.66-5.33-4-8-4z" />
                      </svg>
                      <span className="font-medium">Profile</span>
                    </NavLink>
                    <NavLink
                      to={getSettingsUrl()}
                      className="hidden flex items-center gap-3 px-4 py-2.5 text-[var(--color-text-primary)] hover:bg-[var(--color-surface-hover)] rounded-xl transition-all duration-200"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.983 5.5a1.5 1.5 0 012.841 0l.24.72a1.5 1.5 0 001.933.933l.72-.24a1.5 1.5 0 011.898 1.898l-.24.72a1.5 1.5 0 00.933 1.933l.72.24a1.5 1.5 0 010 2.841l-.72.24a1.5 1.5 0 00-.933 1.933l.24.72a1.5 1.5 0 01-1.898 1.898l-.72-.24a1.5 1.5 0 00-1.933.933l-.24.72a1.5 1.5 0 01-2.841 0l-.24-.72a1.5 1.5 0 00-1.933-.933l-.72.24a1.5 1.5 0 01-1.898-1.898l.24-.72a1.5 1.5 0 00-.933-1.933l-.72-.24a1.5 1.5 0 010-2.841l.72-.24a1.5 1.5 0 00.933-1.933l-.24-.72a1.5 1.5 0 011.898-1.898l.72.24a1.5 1.5 0 001.933-.933l.24-.72zM12 15a3 3 0 100-6 3 3 0 000 6z" />
                      </svg>
                      <span className="font-medium">Settings</span>
                    </NavLink>
                    <div className="my-2 border-t border-[var(--color-border)]" />
                    <button
                      onClick={onLogout}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-red-600 hover:bg-red-50 rounded-xl transition-all duration-200"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      <span className="font-medium">Logout</span>
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <NavLink to="/login">
                  <Button variant="ghost" className="h-8 px-3 text-sm">
                    Sign In
                  </Button>
                </NavLink>
                <div className="relative registration-dropdown">
                  <Button
                    variant="primary"
                    onClick={() => setShowRegisterDropdown(!showRegisterDropdown)}
                    className="h-8 px-4 text-sm"
                  >
                    Get Started
                  </Button>

                  {/* Registration Dropdown */}
                  <div className={`
                    absolute top-full right-0 mt-2 w-64 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-lg overflow-hidden
                    transition-all duration-300 transform origin-top
                    ${showRegisterDropdown
                      ? 'opacity-100 visible scale-y-100 translate-y-0'
                      : 'opacity-0 invisible scale-y-0 -translate-y-2'
                    }
                  `}>
                    <div className="p-3">
                      {/*<h3 className="text-sm font-semibold text-[var(--color-text-primary)] mb-2 text-center">
                        Join SEERAHVERSE
                      </h3>*/}
                      <h1 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3" style={{opacity: 1, transform: 'none'}}><span className="bg-gradient-to-r from-gray-800 via-emerald-600 to-blue-600 bg-clip-text text-transparent">Join</span><br /><span className="bg-gradient-to-r from-blue-600 via-purple-600 to-emerald-600 bg-clip-text text-transparent">SEERAHVERSE</span></h1>
                      <div className="space-y-2">
                        <NavLink to="/signup?type=Student" onClick={() => setShowRegisterDropdown(false)}>
                          <div className="p-3 border border-[var(--color-border)] rounded-lg hover:border-[var(--color-primary)] hover:bg-[var(--color-primary-light)] transition-all cursor-pointer group">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-[var(--color-primary-light)] rounded-md flex items-center justify-center group-hover:bg-[var(--color-primary)]/20 transition-colors">
                                <svg className="w-4 h-4 text-[var(--color-primary)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                </svg>
                              </div>
                              <div className="flex-1">
                                <h4 className="text-sm font-medium text-[var(--color-text-primary)]">Student</h4>
                                <p className="text-xs text-[var(--color-text-secondary)]">
                                  Join learning community
                                </p>
                              </div>
                            </div>
                          </div>
                        </NavLink>

                        <NavLink to="/signup?type=Instructor" onClick={() => setShowRegisterDropdown(false)}>
                          <div className="p-3 border border-[var(--color-border)] rounded-lg hover:border-[var(--color-secondary)] hover:bg-[var(--color-secondary-light)] transition-all cursor-pointer group">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-[var(--color-secondary-light)] rounded-md flex items-center justify-center group-hover:bg-[var(--color-secondary)]/20 transition-colors">
                                <svg className="w-4 h-4 text-[var(--color-secondary)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                                </svg>
                              </div>
                              <div className="flex-1">
                                <h4 className="text-sm font-medium text-[var(--color-text-primary)]">Instructor</h4>
                                <p className="text-xs text-[var(--color-text-secondary)]">
                                  Share your knowledge
                                </p>
                              </div>
                            </div>
                          </div>
                        </NavLink>
                      </div>

                      {/* Divider */}
                      <div className="relative flex justify-center my-2">
                        <div className="absolute inset-x-0 top-1/2 h-px bg-[var(--color-border)] transform -translate-y-1/2 mx-2"></div>
                        <span className="relative z-10 bg-[var(--color-surface)] px-2 text-xs font-medium text-[var(--color-text-secondary)]">OR</span>
                      </div>

                      {/* Already have an account? */}
                      <NavLink to="/login" onClick={() => setShowRegisterDropdown(false)}>
                        <div className="flex items-center p-3 bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] hover:border-[var(--color-text-primary)] hover:shadow-sm transition-all duration-200 group">
                          <div className="w-8 h-8 bg-[var(--color-text-secondary)]/10 rounded-md flex items-center justify-center mr-2 group-hover:bg-[var(--color-text-secondary)]/20 transition-colors">
                            <svg className="w-4 h-4 text-[var(--color-text-secondary)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <h4 className="text-sm font-medium text-[var(--color-text-primary)] group-hover:text-[var(--color-text-primary)] transition-colors">
                              Already have an account?
                            </h4>
                            <p className="text-xs text-[var(--color-text-secondary)] group-hover:text-[var(--color-text-primary)] transition-colors">
                              Log in here
                            </p>
                          </div>
                        </div>
                      </NavLink>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-1.5 rounded-lg transition-colors hover:bg-[var(--color-surface-hover)] border border-[var(--color-border)] h-8 w-8 flex items-center justify-center"
          >
            <svg className="w-4 h-4 text-[var(--color-text-secondary)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isMobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Menu */}
      <div className={`
        lg:hidden fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-[var(--color-surface)] border-l border-[var(--color-border)] shadow-2xl z-50
        transition-transform duration-300 ease-in-out transform
        ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Mobile Menu Header */}
          <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)]">
            <div className="flex items-center gap-2">
              <img
                src="/logo.png"
                alt="SeerahVerse Logo"
                className="h-6 w-auto"
              />
              <span className="font-bold text-sm bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-secondary)] bg-clip-text text-transparent">
                SEERAHVERSE
              </span>
            </div>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="p-2 rounded-lg hover:bg-[var(--color-surface-hover)] transition-colors"
            >
              <svg className="w-5 h-5 text-[var(--color-text-secondary)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Mobile Menu Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {/* Navigation Links */}
            <div className="space-y-1 mb-6">
              {navLinks.map((link) => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`
                    block py-3 px-4 rounded-xl font-medium transition-all duration-200
                    ${location.pathname === link.path
                      ? 'bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-secondary)] text-[var(--color-text-inverse)] shadow-lg'
                      : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-text-primary)]'}
                  `}
                >
                  {link.label}
                </NavLink>
              ))}
            </div>

            {/* Theme Toggle for Mobile */}
            <div className="mb-6">
              <button
                onClick={toggleTheme}
                className="w-full flex items-center justify-between py-3 px-4 rounded-xl text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-text-primary)] transition-all duration-200"
              >
                <span className="font-medium">Theme</span>
                <div className="flex items-center gap-2">
                  <span className="text-lg">{themeEmoji}</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </button>
            </div>

            {/* User Actions */}
            {isAuthenticated && user ? (
              <div className="space-y-2">
                <div className="flex items-center gap-3 p-3 bg-[var(--color-background-secondary)] rounded-xl mb-4">
                  <Avatar
                    src={user.avatar}
                    name={`${user.firstName} ${user.lastName}`}
                    size="sm"
                  />
                  <div>
                    <div className="font-medium text-[var(--color-text-primary)]">{user.firstName} {user.lastName}</div>
                    <div className="text-xs text-[var(--color-text-secondary)]">{user.email}</div>
                  </div>
                </div>
                <NavLink to={getDashboardUrl()} onClick={() => setIsMobileMenuOpen(false)} className="block py-3 px-4 rounded-xl text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-text-primary)] transition-all duration-200">Dashboard</NavLink>
                <NavLink to={getProfileUrl()} onClick={() => setIsMobileMenuOpen(false)} className="block py-3 px-4 rounded-xl text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-text-primary)] transition-all duration-200">Profile</NavLink>
                <NavLink to={getSettingsUrl()} onClick={() => setIsMobileMenuOpen(false)} className="block py-3 px-4 rounded-xl text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-text-primary)] transition-all duration-200">Settings</NavLink>
                <button onClick={() => { onLogout(); setIsMobileMenuOpen(false); }} className="block w-full text-left py-3 px-4 rounded-xl text-red-600 hover:bg-red-50 transition-all duration-200">Logout</button>
              </div>
            ) : (
              <div className="space-y-3">
                <NavLink to="/login" onClick={() => setIsMobileMenuOpen(false)} className="block">
                  <Button variant="outline" fullWidth className="py-3">Sign In</Button>
                </NavLink>
                <Button variant="primary" fullWidth onClick={() => setShowMobileRegistrationModal(true)} className="py-3">
                  Get Started
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
      </div>

      {/* Mobile Registration Modal */}
      <RegistrationModal
        isOpen={showMobileRegistrationModal}
        onClose={() => setShowMobileRegistrationModal(false)}
        onStudentRegister={() => {
          setShowMobileRegistrationModal(false);
          window.location.href = '/signup?type=Student';
        }}
        onInstructorRegister={() => {
          setShowMobileRegistrationModal(false);
          window.location.href = '/signup?type=Instructor';
        }}
        onLogin={() => {
          setShowMobileRegistrationModal(false);
          window.location.href = '/login';
        }}
      />
    </nav>
    </>
  );
};

export { Navbar };
export default Navbar;
